import { Loading, PanelResizeHandleCustom } from 'components'
import { LanguagesDetail } from 'features/AlgoEditor/AlgoEditor.types'
import QuestionSection from 'features/AlgoEditor/components/QuestionSection'
import SettingsModal from 'features/AlgoEditor/components/SettingsModal'
import VideoSection from 'features/LMS/components/VideoSection'
import { usePathname } from 'hooks'
import { AppPage } from 'interfaces'
import { useRouter } from 'next/router'
import { NextSeo } from 'next-seo'
import React, { useCallback, useEffect, useState } from 'react'
import { useQueryClient } from 'react-query'
import { Panel, PanelGroup } from 'react-resizable-panels'
import {
  setEditorWidth,
  setLanguages,
  useAlgoEditorSettings,
  useAppDispatch
} from 'state'
import { useWindowSize } from 'usehooks-ts'
import { logger } from 'utils'

import EditorSection from './components/EditorSection'
import { useAlgoQuestionDetailswithTemplate } from './AlgoEditor.services'
import { LanguageOptions } from './data'

const AlgoEditor: AppPage = () => {
  const { query } = useRouter()
  const queSlug = typeof query.slug === 'string' ? query.slug : ''
  const { isLms } = usePathname()
  const queryClient = useQueryClient()

  // const { data: question, isLoading } = useAlgoChallenge(queSlug)
  //
  // const { data: challengeTemplates } = useChallengeTemplate(question.id)

  const { data, isLoading } = useAlgoQuestionDetailswithTemplate(queSlug)
  const dispatch = useAppDispatch()
  const { width: windowWidth } = useWindowSize()
  const [isResizing, setIsResizing] = useState(false)
  const [settings, updateSettings] = useAlgoEditorSettings()

  const handleEditorResize = useCallback(
    (size: number[]) => {
      logger('width', size)
      const rightPanelWidth = size[1]
      if (rightPanelWidth < 30 || rightPanelWidth > 65 || windowWidth < 1050)
        return

      dispatch(setEditorWidth(rightPanelWidth))
    },
    [dispatch, windowWidth]
  )

  const handleOnSubmitSuccessfully = useCallback(() => {
    if (!isLms) return

    // Invalidates LMS Menu Query when question is submitted successfully
    queryClient.invalidateQueries(['lms-menu'])
  }, [isLms, queryClient])

  const handleOnDragging = (isDragging: boolean) => {
    setIsResizing(isDragging)
  }

  useEffect(() => {
    if (
      data?.question.supported_languages &&
      data.question.supported_languages.length > 0
    ) {
      const allLang: Array<LanguagesDetail> = []

      data.question.supported_languages.forEach((lang) => {
        const localLang = LanguageOptions.find(
          (option) => option.judge_zero_id === lang.judge_zero_id
        )
        if (localLang)
          allLang.push({
            ...localLang,
            ...lang,
            language_id: lang.id,
            id: localLang.id
          })
      })

      if (settings.language.judge_zero_id) {
        const isCurrentSetlanguageInSupportedLanguage = allLang.find(
          (lang) => lang.judge_zero_id === settings.language.judge_zero_id
        )

        if (!isCurrentSetlanguageInSupportedLanguage) {
          updateSettings({
            language: allLang[0]
          })
        }
      }

      dispatch(setLanguages(allLang))
    }
  }, [
    data?.question?.supported_languages,
    dispatch,
    settings.language.judge_zero_id,
    updateSettings
  ])

  if (isLoading || !data) {
    return <Loading centerParent />
  }

  const { question, templates } = data

  return (
    <>
      <NextSeo
        title={`${question?.name || 'Algo Editor'} - Devsnest`}
        description={"Solve this question and other on devsnest's editor"}
      />
      <SettingsModal />
      <PanelGroup
        direction={'horizontal'}
        onLayout={handleEditorResize}
        className="flex-1"
        autoSaveId={'algo-editor'}>
        <Panel
          id="description"
          minSize={30}
          defaultSize={50}
          maxSize={60}
          style={{
            pointerEvents: isResizing ? 'none' : 'auto'
          }}>
          <div className="h-full flex flex-col pb-3 overflow-y-auto">
            {isLms && <VideoSection title={question.name} />}
            <QuestionSection question={question} />
          </div>
        </Panel>

        <PanelResizeHandleCustom onDragging={handleOnDragging} />

        <Panel
          id="editor"
          minSize={40}
          defaultSize={50}
          maxSize={80}
          className="hidden md:block"
          style={{
            pointerEvents: isResizing ? 'none' : 'auto'
          }}>
          <EditorSection
            questionId={question.id}
            challengeTemplates={templates}
            showNextChallenge={!isLms}
            onSubmitSuccessFully={handleOnSubmitSuccessfully}
          />
        </Panel>
      </PanelGroup>
    </>
  )
}

AlgoEditor.layout = {
  pageClassName: 'scroll-hide p-0',
  screenHeight: true,
  screenWidth: true,
  hideFooter: true,
  navProps: {
    fullWidth: true
  }
}

AlgoEditor.seo = {
  title: 'Solve DSA Question | Online Coding Editor | Devsnest'
}

export default AlgoEditor
